import {
  resetZoom,
  downloadCytoJSON,
  downloadCytoPNG,
  hideAllEdgeLabels,
  showAllEdgeLabels,
} from './ActionButtonsFunctions';
import SharedButton from '@/global/Button/Button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { useEffect, useRef, useState } from 'react';

const possibleCytoLayouts = ['circle', 'concentric', 'grid', 'breadthfirst', 'random', 'cose'];

export default function CytoscapeActionButtons({
  changeSelectedLayout,
  selectedLayout,
  names,
  parent,
  chartInfo,
}) {
  const [showEdgeLabels, setShowEdgeLabels] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const layoutDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setShowActionsDropdown(false);
      }
      if (layoutDropdownRef.current && !layoutDropdownRef.current.contains(event.target as Node)) {
        setShowLayoutDropdown(false);
      }
    };

    if (showActionsDropdown || showLayoutDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActionsDropdown, showLayoutDropdown]);

  const handleShowEdgeLabels = () => {
    if (showEdgeLabels) {
      hideAllEdgeLabels();
    } else {
      showAllEdgeLabels();
    }
    setShowEdgeLabels(!showEdgeLabels);
  };

  const handleSelectLayout = layout => {
    changeSelectedLayout(layout);
    setShowLayoutDropdown(false);
  };

  return (
    <div className="flex flex-col md:flex-row justify-center gap-2 mb-4">
      <div className="flex gap-2 justify-center">
        <SharedButton variant="secondary" onClick={handleShowEdgeLabels}>
          {showEdgeLabels ? 'Hide edge labels' : 'Show edge labels'}
        </SharedButton>
      </div>
      <div className="flex gap-2 justify-center">
        <SharedButton variant="secondary" onClick={resetZoom}>
          Reset Zoom
        </SharedButton>
        <div style={{ position: 'relative' }} ref={actionsDropdownRef}>
          <SharedButton
            variant="secondaryOutline"
            onClick={() => {
              setShowLayoutDropdown(false);
              setShowActionsDropdown(prev => !prev);
            }}
            className="mr-0"
          >
            <ArrowDownTrayIcon className="w-5 mr-2" />
            Download
            <ChevronDownIcon
              className="w-4 ml-2 transition-transform duration-200"
              style={{ transform: showActionsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </SharedButton>
          {showActionsDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
              <span
                className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                onClick={() => {
                  downloadCytoJSON({ names, parent, chartInfo });
                  setShowActionsDropdown(false);
                }}
              >
                JSON
              </span>
              <span
                className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                onClick={() => {
                  downloadCytoPNG({ names, parent, chartInfo });
                  setShowActionsDropdown(false);
                }}
              >
                PNG
              </span>
            </div>
          )}
        </div>
        <div style={{ position: 'relative' }} ref={layoutDropdownRef}>
          <SharedButton
            variant="secondaryOutline"
            onClick={() => {
              setShowActionsDropdown(false);
              setShowLayoutDropdown(prev => !prev);
            }}
            className="mr-0"
          >
            <img
              src="/static/images/icons/layoutIcon.svg"
              alt="Layout"
              className="w-5 mr-2 layout-icon-white-on-hover"
            />
            Layout
            <ChevronDownIcon
              className="w-4 ml-2 transition-transform duration-200"
              style={{ transform: showLayoutDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </SharedButton>
          {showLayoutDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
              {possibleCytoLayouts.map(layout => (
                <span
                  key={layout}
                  className={`cursor-pointer row items-center justify-center font-bold p-2 rounded-md text-blue w-full hover:bg-blue-100 ${
                    selectedLayout === layout ? 'bg-blue-100' : ''
                  }`}
                  onClick={() => handleSelectLayout(layout)}
                >
                  {layout}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
